import {
  Breakpoints,
  compact,
  type HeaderButton,
  HeaderEntryTextColor,
  type HeaderLink,
  IconCalendar,
  IconEmail,
  IconFolderPlus,
  IconGraduateHat,
  IconProfile,
  IconQuestion,
  IconRefund,
  IconSignOut,
  IconSwitch,
  IconWarningTriangle,
  type MobileNavEntry,
  type NavProps,
  NavTheme,
  PageHeader,
  type SubNavProps,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import {
  ButtonColor,
  ButtonSize,
  IconGift,
} from '@leland-dev/leland-ui-library';
import { useRouter } from 'next/compat/router';
import dynamic from 'next/dynamic';
import React, { type FC, useMemo } from 'react';

import { useAuth, useLogout } from '../../context/AuthContext';
import {
  COACH_PROFILE_URL,
  COACH_SITE_URL,
  DASHBOARD_URL,
  EVENTS_RELATIVE_URL,
  LELAND_PLUS_REDIRECT_URL,
  ORDER_HISTORY_URL,
  PARTNERSHIPS_LP_URL,
  SITE_URL,
  SUPPORT_URL,
} from '../../utils/constants';
import { LelandImpersonation } from '../../utils/impersonation';
import { BASE_SEARCH_PATH, generateSearchPath } from '../../utils/search';

import {
  type LulNavbarNavCategoryListingFragment,
  useLulNavbarUserQuery,
} from './__generated-gql-types__/LulNavbar.generated';

const InboxIconWithUnreadIndicator = dynamic(
  () =>
    import('./InboxIconWithUnreadIndicator').then(
      (mod) => mod.InboxIconWithUnreadIndicator,
    ),
  { ssr: false },
);

export interface LulNavbarProps {
  navCategories: Possible<LulNavbarNavCategoryListingFragment[]>;
  hideSubnav?: boolean;
  minimalNav?: boolean;
}

export const LulNavbar: FC<LulNavbarProps> = ({
  navCategories,
  hideSubnav,
  minimalNav,
}) => {
  const router = useRouter();
  const { isImpersonating } = useAuth();
  const { data: userData, loading: userLoading } = useLulNavbarUserQuery();
  const { logout, logoutLoading } = useLogout();
  const isSm = useMediaQuery(Breakpoints.SM);
  const isMd = useMediaQuery(Breakpoints.MD);

  const isCoach = !!userData?.user?.coach;
  const isApplicant = !!userData?.user?.applicant;

  const coachLink: HeaderLink = useMemo(() => {
    return isCoach
      ? {
          label: 'Switch to Coaching',
          url: COACH_SITE_URL,
        }
      : {
          label: 'Become a Coach',
          url: `${SITE_URL}/become-a-coach`,
          isExternal: true,
        };
  }, [isCoach]);

  const IMPERSONATION_HEADER_ENTRY: HeaderButton = useMemo(
    () => ({
      label: 'EXIT IMPERSONATION',
      LeftIcon: IconWarningTriangle,
      textColor: HeaderEntryTextColor.RED,
      onClick: () => {
        LelandImpersonation.removeImpersonatedUserId();

        void router?.push(`${COACH_SITE_URL}/internal/ops/customers`);
      },
    }),
    [router],
  );

  const subnavbarCategories: NavProps['categories'] =
    navCategories?.map(({ name, goal, categories }) => {
      return {
        url: goal
          ? generateSearchPath({
              goal,
              categorySlug: null,
              subCategorySlug: null,
            })
          : undefined,
        label: name,
        subItems: categories?.map(
          ({ name, goal, slug, subCategoryList, shouldShowSubCategories }) => {
            return {
              url: generateSearchPath({
                goal,
                categorySlug: slug,
                subCategorySlug: null,
              }),
              label: name,
              subItems: shouldShowSubCategories
                ? subCategoryList?.map(({ name, slug: subCategorySlug }) => {
                    return {
                      url: generateSearchPath({
                        goal,
                        categorySlug: slug,
                        subCategorySlug,
                      }),
                      label: name,
                    };
                  })
                : undefined,
            };
          },
        ),
      };
    }) ?? [];

  const isActiveLelandPlus = userData?.user?.applicant?.hasLelandPlusAccess;

  const profileMenuItemSections: MobileNavEntry[][] = useMemo(() => {
    const topSection: MobileNavEntry[] = [
      {
        url: userData?.user?.applicant ? '/settings' : COACH_PROFILE_URL,
        LeftIcon: IconProfile,
        label: 'Profile',
      },
    ];
    if (userData?.user?.applicant) {
      topSection.push({
        url: ORDER_HISTORY_URL,
        LeftIcon: IconRefund,
        label: 'Order History',
      });
    }
    if (!isMd) {
      topSection.unshift({
        label: 'Free events',
        url: EVENTS_RELATIVE_URL,
        isExternal: false,
        LeftIcon: IconCalendar,
      });

      if (userData?.user) {
        if (isActiveLelandPlus) {
          topSection.unshift({
            label: 'Leland+',
            url: LELAND_PLUS_REDIRECT_URL,
            LeftIcon: IconFolderPlus,
            isExternal: true,
          });
        }
      }
    }
    const sections: Array<Nullable<Array<Nullable<MobileNavEntry>>>> = [
      isImpersonating ? [IMPERSONATION_HEADER_ENTRY] : [],
      minimalNav ? [] : topSection,
      [
        ...(minimalNav
          ? []
          : [
              {
                ...coachLink,
                LeftIcon: isCoach ? IconSwitch : IconGraduateHat,
              },
            ]),
        {
          url: SUPPORT_URL,
          isExternal: true,
          LeftIcon: IconQuestion,
          label: 'Help & Support',
        },
        {
          onClick: (e) => {
            e.preventDefault();
            void logout();
          },
          LeftIcon: IconSignOut,
          label: 'Log Out',
        },
      ],
    ];

    return sections.reduce<MobileNavEntry[][]>(
      (reducedSections, currentSection) => {
        const filteredCurrentSection = currentSection?.filter(compact);
        if (filteredCurrentSection?.length) {
          reducedSections.push(filteredCurrentSection);
        }
        return reducedSections;
      },
      [],
    );
  }, [
    minimalNav,
    userData?.user,
    isMd,
    isImpersonating,
    IMPERSONATION_HEADER_ENTRY,
    coachLink,
    isCoach,
    isActiveLelandPlus,
    logout,
  ]);

  const leftLinks: NavProps['leftLinks'] =
    isSm && isImpersonating ? [IMPERSONATION_HEADER_ENTRY] : undefined;

  const rightLinks: NavProps['rightLinks'] = useMemo(() => {
    if (minimalNav) {
      return undefined;
    }

    const links: NavProps['rightLinks'] = [];
    if (isSm && isApplicant) {
      links.push({ label: 'Dashboard', url: DASHBOARD_URL });
    }

    if (isMd) {
      if (isApplicant && isActiveLelandPlus) {
        links.push({
          label: 'Leland+',
          url: LELAND_PLUS_REDIRECT_URL,
          isExternal: true,
        });
      }
      links.push({
        label: 'Free events',
        url: EVENTS_RELATIVE_URL,
        isExternal: false,
      });
    }

    if (isApplicant) {
      links.push({
        label: 'Inbox',
        CustomLeftIcon: InboxIconWithUnreadIndicator as FC<{
          iconClassName?: string;
        }>,
        LeftIcon: IconEmail,
        url: `/inbox`,
        hideLabel: true,
        rounded: true,
      });
      if (isSm) {
        links.push({
          label: 'Refer',
          LeftIcon: IconGift,
          url: '/referrals',
          buttonColor: ButtonColor.BLACK,
          size: ButtonSize.SMALL,
        });
      }
    } else if (isSm) {
      links.push(coachLink);
    } else {
      links.push({
        label: 'Join',
        url: `/signup`,
      });
    }
    return links;
  }, [coachLink, isActiveLelandPlus, isApplicant, isMd, isSm, minimalNav]);

  const mobileSidebarLinks: MobileNavEntry[] = useMemo(() => {
    if (minimalNav) {
      return [
        {
          label: 'Log out',
          url: '/logout',
          LeftIcon: IconSignOut,
        },
      ];
    }
    return (
      [
        isImpersonating ? IMPERSONATION_HEADER_ENTRY : null,
        isApplicant
          ? {
              label: 'Dashboard',
              url: DASHBOARD_URL,
            }
          : null,
        isApplicant
          ? {
              label: 'Inbox',
              url: '/inbox',
            }
          : null,
        {
          label: 'Free events',
          url: EVENTS_RELATIVE_URL,
          isExternal: false,
        },
        isActiveLelandPlus
          ? {
              label: 'Leland+',
              url: LELAND_PLUS_REDIRECT_URL,
              isExternal: true,
            }
          : null,
      ] satisfies Array<Nullable<MobileNavEntry>>
    ).filter(compact);
  }, [
    IMPERSONATION_HEADER_ENTRY,
    isImpersonating,
    isActiveLelandPlus,
    minimalNav,
    isApplicant,
  ]);

  const accountLinks: MobileNavEntry[] = useMemo(() => {
    if (minimalNav) {
      return [];
    }
    return userData
      ? [
          {
            label: 'Profile',
            url: userData?.user?.applicant ? '/settings' : COACH_PROFILE_URL,
          },
          {
            label: 'Refer a friend',
            url: '/referrals',
          },
          {
            label: 'Order History',
            url: ORDER_HISTORY_URL,
          },
          {
            label: 'Payment Details',
            url: '/settings/payment-details',
          },
          {
            label: 'Log out',
            onClick: (e) => {
              e.preventDefault();
              void logout();
            },
            textColor: HeaderEntryTextColor.RED,
          },
        ]
      : [];
  }, [minimalNav, userData, logout]);

  const LulNavProps: NavProps = {
    categories: subnavbarCategories,
    theme: isImpersonating ? NavTheme.RED : undefined,
    mobileLinks: {
      sidebar: mobileSidebarLinks,
      footer: [
        {
          label: 'Home',
          url: '/',
        },
        coachLink,
        {
          label: 'Refer',
          url: '/referrals',
        },
        {
          label: 'Partnerships',
          url: PARTNERSHIPS_LP_URL,
          isExternal: true,
        },
        {
          label: 'Support',
          url: SUPPORT_URL,
          isExternal: true,
        },
      ],
      browseCoaching: subnavbarCategories,
      accountMenu: accountLinks,
    },
    urls: {
      home: BASE_SEARCH_PATH,
      login: `/login`,
      signup: `/signup`,
      inbox: `/inbox`,
    },
    profileMenuItemSections,
    rightLinks,
    user: userData?.user
      ? {
          ...userData.user,
          profileLink: userData.user.applicant
            ? '/settings'
            : COACH_PROFILE_URL,
        }
      : undefined,
    userLoading: userLoading || logoutLoading,
    leftLinks,
  };
  const LulSubnavProps: Optional<SubNavProps> =
    hideSubnav || !subnavbarCategories?.length
      ? undefined
      : {
          items: subnavbarCategories,
        };

  return (
    <PageHeader
      sticky={false}
      navProps={LulNavProps}
      subNavProps={LulSubnavProps}
    />
  );
};
